import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from "../images/Quick-Care.png";
import Profile from "../images/profile.png";
import MenuIcon from '@mui/icons-material/Menu';
import ClearIcon from '@mui/icons-material/Clear';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

const Header = ({ isSidebarOpen, toggleSidebar }) => {
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
        setIsNotificationsOpen(false);
    };

    const toggleNotifications = () => {
        setIsNotificationsOpen(!isNotificationsOpen);
        setIsDropdownOpen(false);
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
        setIsDropdownOpen(false);
    };

    const handleSettings = () => {
        navigate('/settings');
        setIsDropdownOpen(false);
    };

    return (
        <header className='w-full bg-white border-b border-gray-200 shadow-sm'>
            <div className='flex items-center justify-between px-3 sm:px-4 lg:px-6 py-3 lg:py-4'>
                {/* Left Section - Menu Toggle & Logo */}
                <div className='flex items-center gap-2 lg:gap-4'>
                    {/* Menu Toggle - Visible on all screens, but behaves differently */}
                    <button 
                        onClick={toggleSidebar}
                        className='p-1.5 rounded-lg hover:bg-gray-100 transition-colors duration-200 focus:outline-none md:hidden'
                        aria-label="Toggle sidebar"
                    >
                        {isSidebarOpen ? (
                            <ClearIcon className='text-gray-600 text-2xl' />
                        ) : (
                            <MenuIcon className='text-gray-600 text-2xl' />
                        )}
                    </button>

                    {/* Logo */}
                    <div className='flex items-center gap-2'>
                        <img 
                            src={Logo} 
                            alt="QuickCare Logo" 
                            className='h-8 sm:h-10 lg:h-12 w-auto object-contain'
                        />
                        
                    </div>
                </div>

                {/* Right Section - Notifications & Profile */}
                <div className='flex items-center gap-2 sm:gap-3 lg:gap-4'>
                    {/* Notifications */}
                    <div className='relative'>
                        <button 
                            onClick={toggleNotifications}
                            className='relative p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 focus:outline-none'
                            aria-label="Notifications"
                        >
                            <NotificationsNoneIcon className='text-gray-600 text-xl sm:text-2xl' />
                            <span className='absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border-2 border-white animate-pulse'></span>
                        </button>

                        {/* Notifications Dropdown */}
                        {isNotificationsOpen && (
                            <div className='absolute right-0 mt-2 w-72 sm:w-80 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50'>
                                <div className='p-3 border-b border-gray-100'>
                                    <h3 className='text-sm font-semibold text-gray-800'>Notifications</h3>
                                </div>
                                <div className='max-h-64 overflow-y-auto'>
                                    <div className='p-3 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50'>
                                        <p className='text-sm text-gray-700'>New patient registered</p>
                                        <p className='text-xs text-gray-400'>2 minutes ago</p>
                                    </div>
                                    <div className='p-3 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50'>
                                        <p className='text-sm text-gray-700'>Payment received</p>
                                        <p className='text-xs text-gray-400'>1 hour ago</p>
                                    </div>
                                    <div className='p-3 hover:bg-gray-50 transition-colors cursor-pointer'>
                                        <p className='text-sm text-gray-700'>New admin invite</p>
                                        <p className='text-xs text-gray-400'>3 hours ago</p>
                                    </div>
                                </div>
                                <div className='p-2 border-t border-gray-100 text-center'>
                                    <button className='text-sm text-[#069125] font-medium hover:underline'>
                                        View all notifications
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Divider */}
                    <div className='hidden sm:block w-px h-8 bg-gray-200'></div>

                    {/* Profile Section */}
                    <div className='relative'>
                        <div 
                            onClick={toggleDropdown}
                            className='flex items-center gap-1 sm:gap-2 cursor-pointer hover:bg-gray-50 rounded-xl px-2 py-1 transition-colors duration-200'
                        >
                            <div className='relative'>
                                <img 
                                    src={Profile} 
                                    alt="Profile" 
                                    className='w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-[#069125]/20 hover:border-[#069125] transition-colors duration-200'
                                />
                                <span className='absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white'></span>
                            </div>
                            <div className='hidden lg:block'>
                                <p className='text-sm font-semibold text-gray-700 leading-tight'>Admin User</p>
                                <p className='text-xs text-gray-400 leading-tight'>Administrator</p>
                            </div>
                            <ArrowDropDownIcon className={`hidden lg:block text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </div>

                        {/* Profile Dropdown */}
                        {isDropdownOpen && (
                            <div className='absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50'>
                                <div className='p-3 border-b border-gray-100 flex items-center gap-3'>
                                    <img 
                                        src={Profile} 
                                        alt="Profile" 
                                        className='w-10 h-10 rounded-full object-cover'
                                    />
                                    <div>
                                        <p className='text-sm font-semibold text-gray-800'>Admin User</p>
                                        <p className='text-xs text-gray-400'>admin@quickcare.com</p>
                                    </div>
                                </div>
                                <div className='p-1'>
                                    <button 
                                        onClick={handleSettings}
                                        className='w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors'
                                    >
                                        <SettingsIcon className='text-gray-400 text-sm' />
                                        Settings
                                    </button>
                                    
                                    <div className='border-t border-gray-100 my-1'></div>
                                    
                                    <button 
                                        onClick={handleLogout}
                                        className='w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors'
                                    >
                                        <ExitToAppIcon className='text-red-400 text-sm' />
                                        Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Profile Info - Visible only on small screens */}
            <div className='lg:hidden px-4 pb-3 flex items-center gap-2 border-t border-gray-100 pt-2'>
                <div className='relative'>
                    <img 
                        src={Profile} 
                        alt="Profile" 
                        className='w-8 h-8 rounded-full object-cover border border-[#069125]/20'
                    />
                    <span className='absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border-2 border-white'></span>
                </div>
                <div>
                    <p className='text-sm font-semibold text-gray-700 leading-tight'>Admin User</p>
                    <p className='text-xs text-gray-400 leading-tight'>Administrator</p>
                </div>
            </div>
        </header>
    );
};

export default Header;